import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Empty, Tag, Tooltip } from "antd";
import { ReloadOutlined, LinkOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { statusPageApi } from "../../services/api";
import { ChartSkeleton } from "../Analytics/components/ChartParts";

dayjs.extend(relativeTime);

const OVERALL = {
  operational: { label: "All Systems Operational", cls: "operational" },
  degraded: { label: "Degraded Performance", cls: "degraded" },
  partial_outage: { label: "Partial System Outage", cls: "down" },
  major_outage: { label: "Major System Outage", cls: "down" },
};

const SERVICE = {
  operational: { label: "Operational", color: "success" },
  degraded: { label: "Degraded", color: "warning" },
  down: { label: "Down", color: "error" },
  paused: { label: "Paused", color: "default" },
  unknown: { label: "No data", color: "default" },
};

const REFRESH_MS = 60_000;

function dayClass(uptime) {
  if (uptime == null) return "nodata";
  if (uptime >= 99.5) return "operational";
  if (uptime >= 95) return "degraded";
  return "down";
}

const GROUP_ORDER = ["PROD", "CCS", "ATNM"];

function groupServices(services) {
  const groups = {};
  for (const s of services) (groups[s.group] ||= []).push(s);
  const rank = (g) => { const i = GROUP_ORDER.indexOf(g); return i === -1 ? GROUP_ORDER.length : i; };
  return Object.entries(groups).sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
}

function fmtPct(v) {
  return v == null ? "—" : `${v.toFixed(v >= 99.95 ? 3 : 2)}%`;
}

function UptimeBar({ history }) {
  return (
    <div className="status-bar">
      {history.map((d) => (
        <Tooltip
          key={d.date}
          title={
            <>
              <div>{dayjs(d.date).format("D MMM YYYY")}</div>
              <div>{d.uptime == null ? "No checks" : `${fmtPct(d.uptime)} uptime · ${d.checks} checks`}</div>
            </>
          }
        >
          <span className={`status-bar-day ${dayClass(d.uptime)}`} />
        </Tooltip>
      ))}
    </div>
  );
}

function ServiceRow({ s }) {
  const st = SERVICE[s.status] || SERVICE.unknown;
  return (
    <div className="status-service">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`status-dot ${s.status}`} />
            <span className="font-semibold text-[14px] text-slate-800 truncate">{s.name.replace(/^\[[^\]]+\]\s*/, "")}</span>
            {s.url && (
              <a href={s.url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-indigo-600 text-[12px]">
                <LinkOutlined />
              </a>
            )}
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5 flex gap-2 flex-wrap">
            <span>{s.type}</span>
            {s.periodMinutes && <span>every {s.periodMinutes} min</span>}
            {s.avgDurationMs != null && <span>{Math.round(s.avgDurationMs)} ms avg</span>}
            {s.lastCheckedAt && <span>checked {dayjs(s.lastCheckedAt).fromNow()}</span>}
          </div>
        </div>
        <Tag color={st.color} className="!m-0">{st.label}</Tag>
      </div>
      <UptimeBar history={s.history} />
      <div className="flex justify-between text-[11px] text-slate-400 mt-1">
        <span className="status-bar-start">90 days ago</span>
        <span className="status-bar-start-mobile">30 days ago</span>
        <span>{fmtPct(s.uptime90d)} uptime</span>
        <span>Today</span>
      </div>
    </div>
  );
}

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const fetchedAt = useRef(0);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const res = await statusPageApi.getStatus();
      if (!mounted.current) return;
      setData(res.data);
      setError(null);
      fetchedAt.current = Date.now();
    } catch (err) {
      if (!mounted.current) return;
      setError(err.response?.data?.error || err.message);
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, []);

  // Refresh every 60s while the tab is visible. A timeout chain (not
  // setInterval) means the next tick is scheduled only after the previous
  // fetch settles, so slow responses never overlap. Hidden tabs pause and
  // catch up immediately on return if the data is stale.
  useEffect(() => {
    mounted.current = true;
    let timer;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(tick, REFRESH_MS);
    };
    const tick = async () => {
      if (document.visibilityState === "visible") await load();
      schedule();
    };
    const onWake = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - fetchedAt.current >= REFRESH_MS) {
        load();
        schedule();
      }
    };
    load();
    schedule();
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("online", onWake);
    return () => {
      mounted.current = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("online", onWake);
    };
  }, [load]);

  const overall = data ? OVERALL[data.overall] || OVERALL.operational : null;

  return (
    <div>
      <div className="page-header flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="page-title">System Status</h1>
          <p className="page-subtitle">
            Live health of production services from New Relic synthetic monitors
            {data && <> · updated {dayjs(data.generatedAt).format("HH:mm:ss")} · refreshes every {REFRESH_MS / 1000}s</>}
          </p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading && !!data}>Refresh</Button>
      </div>

      {error && (
        <Alert type="error" showIcon className="mb-4" message="Could not load status" description={error}
          action={<Button size="small" onClick={load}>Retry</Button>} />
      )}

      {!data && loading && <ChartSkeleton type="table" height={300} />}

      {data && (
        <>
          <div className={`status-banner ${overall.cls}`}>
            <span className={`status-dot lg ${overall.cls}`} />
            <span className="text-[18px] font-semibold">{overall.label}</span>
          </div>

          <div className="status-legend">
            <span><i className="status-bar-day operational" /> ≥ 99.5%</span>
            <span><i className="status-bar-day degraded" /> 95–99.5%</span>
            <span><i className="status-bar-day down" /> &lt; 95%</span>
            <span><i className="status-bar-day nodata" /> no checks</span>
          </div>

          {data.services.length === 0 && <Empty description="No synthetic monitors found" />}
          {groupServices(data.services).map(([group, services]) => (
            <section key={group} className="mb-6">
              <h2 className="status-group-title">
                {group}
                <span className="status-group-count">{services.filter((s) => s.status === "operational").length}/{services.length} operational</span>
              </h2>
              <div className="status-services">
                {services.map((s) => <ServiceRow key={s.guid} s={s} />)}
              </div>
            </section>
          ))}

          <h2 className="text-[16px] font-semibold text-slate-800 mt-8 mb-3">Recent failed checks (7 days)</h2>
          {data.incidents.length === 0 ? (
            <div className="status-service text-[13px] text-slate-500">No failed checks in the last 7 days.</div>
          ) : (
            <div className="status-incidents">
              {data.incidents.map((i, idx) => (
                <div key={idx} className="status-incident">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-semibold text-[13px] text-slate-800">{i.monitorName}</span>
                    <span className="text-[12px] text-slate-400">{dayjs(i.at).format("D MMM, HH:mm")}</span>
                  </div>
                  <div className="text-[12px] text-slate-500 mt-0.5">
                    <Tag color="error" className="!m-0 !mr-2 !text-[11px]">{i.result}</Tag>
                    {i.location}
                    {i.error && <span className="block mt-1 font-[monospace] text-[11px] text-slate-600 break-all">{i.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
